<script setup lang="ts">
import { onMounted, computed, ref, watch, nextTick } from 'vue';
import { BaseButton, BaseCircleButton, BaseIcon, BaseOverlay } from '@/components/Base';
import { isIntegratedMode } from '@/config';
import { useVideo } from '@/composables/useVideo';
import { useDeviceOrientation } from '@/composables/useDeviceOrientation';
import VideoState from './VideoState.vue';
import CameraPermissionTutorial from './CameraPermissionTutorial.vue';
import { VideoState as VideoStateEnum } from '@/types/VideoState';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Landscape orientation detection
const { isLandscape } = useDeviceOrientation();

// Refs for DOM elements
const video = ref<HTMLVideoElement | null>(null);

const {
  switchFacingMode,
  toggleTorch,
  videoState,
  cameraStream,
  startCamera,
  stopCamera,
  cameraWasEverGranted,
  videoEverActivated,
  torchAvailable,
  torchOn,
  switchCameraInProgress,
  toggleTorchInProgress,
} = useVideo();

const integratedBannerKey = computed<string | null>(() => {
  if (!isIntegratedMode || !videoEverActivated.value) return null;
  switch (videoState.value) {
    case VideoStateEnum.Closed:       return 'video.videoEnded';
    case VideoStateEnum.Disconnected: return 'video.state.disconnected';
    case VideoStateEnum.Failed:       return 'video.state.failed';
    default:                          return null;
  }
});

const cameraEnabled = computed(() => cameraStream.value != null);

async function applyStreamToVideoEl() {
  if (!video.value) {
    return;
  }

  try {
    video.value.srcObject = cameraStream.value;
    await video.value.play();
  } catch (e) {
    console.warn("Failed to apply stream to video element", e);
  }
}

watch(cameraStream, async () => {
  console.log("Camera stream changed in NgVideo.vue:", cameraStream.value);
  if (cameraStream.value == null) {
    // TODO ošetřit tenhle stav a nastylovat dle něj video obrazovku
    // return;
  }

  await nextTick(); // Wait for DOM to update (v-if toggle)
  await applyStreamToVideoEl();
}, { deep: true, immediate: true });

onMounted(async () => {
  // if stream already set before component mounted, ensure it's applied
  await applyStreamToVideoEl();
});

async function toggleCameraEnabled() {
  if (cameraEnabled.value) {
    await stopCamera();
  } else {
    await startCamera();
  }
}
</script>

<template>
  <div class="video-view" data-testid="video-call-view">
    <!-- Landscape orientation warning overlay -->
    <BaseOverlay v-if="isLandscape && !isIntegratedMode" data-testid="video-rotate-overlay">
      <BaseIcon name="rotate-phone" :size="64" color="white" />
      <p class="landscape-overlay__text">{{ t('video.rotateDevice') }}</p>
    </BaseOverlay>

    <!-- Status overlay (integrated mode) -->
    <BaseOverlay v-if="integratedBannerKey">
      <BaseIcon name="end-video" :size="48" color="white" />
      <p class="video-ended__text">{{ t(integratedBannerKey!) }}</p>
    </BaseOverlay>

    <!-- Top Navigation -->
    <nav class="top-actions" :class="!isIntegratedMode ? 'actions-wrapper' : 'actions-wrapper-integrated'">
      <div v-if="!isIntegratedMode && torchAvailable" class="top-action-buttons top-action-buttons--start">
        <BaseCircleButton
          :icon="torchOn ? 'torch-on' : 'torch'"
          variant="primary"
          :size="60"
          :icon-size="36"
          :disabled="toggleTorchInProgress"
          :aria-label="torchOn ? t('video.turnOffTorch') : t('video.turnOnTorch')"
          data-testid="video-torch-button"
          @click="toggleTorch"
        />
      </div>
      <div v-else></div>
      <VideoState :state="videoState" :is-integrated-mode="isIntegratedMode" />
      <div v-if="!isIntegratedMode" class="top-action-buttons top-action-buttons--end">
        <BaseCircleButton
          icon="camera-switch"
          :size="60"
          :icon-size="36"
          :disabled="switchCameraInProgress"
          :aria-label="t('video.switchCamera')"
          data-testid="video-switch-camera-button"
          @click="switchFacingMode"
        />
      </div>
      <div v-else></div>
    </nav>

    <!-- Video Container -->
    <div class="video-container" v-if="cameraStream">
      <video ref="video" id="localVideo" autoplay muted playsinline data-testid="video-self-view"></video>
      <canvas ref="canvas" id="canvas"></canvas>
    </div>

    <div class="video-container no-camera" v-else>
      <div v-if="!isIntegratedMode"> <!-- do not show in integrated mode when video ended / failed -->
        <div v-if="cameraWasEverGranted" class="camera-paused" data-testid="video-camera-paused-text">
          <BaseIcon name="end-video" :size="64" />
          <p class="camera-paused__text">{{ t('video.cameraPaused') }}</p>
        </div>
        <CameraPermissionTutorial v-else />
      </div>
      <div v-else-if="!integratedBannerKey" class="camera-loading" role="status">
        <span class="camera-loading__spinner"></span>
      </div>
    </div>

    <!-- Control buttons -->
    <div v-if="!isIntegratedMode" class="actions-wrapper bottom-actions">
      <BaseButton
        :left-icon="cameraEnabled ? 'end-video' : 'start-video'"
        :variant="cameraEnabled ? 'danger' : 'success'"
        :hold-to-confirm="cameraEnabled"
        size="medium"
        data-testid="video-camera-toggle-button"
        @click="toggleCameraEnabled"
      >
        <p v-if="cameraEnabled">{{ t('video.turnOffCamera') }}</p>
        <p v-else>{{ t('video.turnOnCamera') }}</p>
      </BaseButton>
    </div>
  </div>
</template>

<style scoped>
.actions-wrapper {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 10;
  padding: var(--spacing-md);
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  align-items: center;
}

.actions-wrapper-integrated {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 10;
  padding: var(--spacing-md);
}

.top-actions {
  top: 0;
}

.top-action-buttons {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.top-action-buttons--start {
  justify-content: flex-start;
}

.top-action-buttons--end {
  justify-content: flex-end;
}

.bottom-actions {
  justify-content: center;
  bottom: var(--spacing-xl, 24px);
  grid-template-columns: repeat(1, auto);
  padding: var(--spacing-lg) var(--spacing-md);
}

.video-view {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: var(--color-video-background);
}

.video-container {
  position: relative;
  flex: 1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-camera {
  padding: var(--spacing-md);
  text-align: center;
}

#localVideo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

#canvas {
  display: none;
}

.landscape-overlay__text {
  color: var(--color-text-light);
  font-size: var(--font-size-lg);
  max-width: 300px;
  line-height: 1.4;
}

.video-ended__text {
  color: var(--color-text-light);
  font-size: var(--font-size-md);
  font-weight: 600;
  max-width: 280px;
  line-height: 1.4;
  text-align: center;
}

.camera-paused {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  text-align: center;
  color: var(--color-text-light, #fff);
}

.camera-paused__text {
  font-size: var(--font-size-lg);
  font-weight: 600;
  max-width: 280px;
  line-height: 1.4;
}

.camera-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-light, #fff);
}

.camera-loading__spinner {
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
